// Iter61d — Web Push subscription toggle (PWA notifications).
// Handles: permission request, VAPID key fetch, PushManager.subscribe, POST to backend.
// Displays helpful iOS "Add to Home Screen" hint when running in Safari (not standalone).
import { useEffect, useState } from 'react';
import { toast } from 'sonner';
import { api } from '../lib/api';
import { Bell, BellOff, Share } from './icons';


function urlBase64ToUint8Array(base64) {
  const padding = '='.repeat((4 - base64.length % 4) % 4);
  const b64 = (base64 + padding).replace(/-/g, '+').replace(/_/g, '/');
  const raw = window.atob(b64);
  const out = new Uint8Array(raw.length);
  for (let i = 0; i < raw.length; i++) out[i] = raw.charCodeAt(i);
  return out;
}


export function PushToggle({ compact = false }) {
  const [supported, setSupported] = useState(false);
  const [permission, setPermission] = useState('default');
  const [subscribed, setSubscribed] = useState(false);
  const [loading, setLoading] = useState(false);
  const [isIos, setIsIos] = useState(false);
  const [isStandalone, setIsStandalone] = useState(false);

  useEffect(() => {
    const ua = window.navigator.userAgent.toLowerCase();
    const ios = /iphone|ipad|ipod/.test(ua);
    const standalone =
      window.matchMedia('(display-mode: standalone)').matches ||
      window.navigator.standalone === true;
    setIsIos(ios);
    setIsStandalone(standalone);
    const sup = 'serviceWorker' in navigator && 'PushManager' in window && 'Notification' in window;
    setSupported(sup);
    if (sup) setPermission(Notification.permission);
    // Detect current subscription
    (async () => {
      try {
        if (!sup) return;
        const reg = await navigator.serviceWorker.ready;
        const sub = await reg.pushManager.getSubscription();
        setSubscribed(!!sub);
      } catch { /* ignore */ }
    })();
  }, []);

  const enable = async () => {
    setLoading(true);
    try {
      const perm = await Notification.requestPermission();
      setPermission(perm);
      if (perm !== 'granted') {
        toast.error('Notifications refusées. Autorise-les dans les réglages Safari/Chrome.');
        return;
      }
      const { data } = await api.get('/push/vapid-public-key');
      const applicationServerKey = urlBase64ToUint8Array(data.public_key);
      const reg = await navigator.serviceWorker.ready;
      const sub = await reg.pushManager.subscribe({
        userVisibleOnly: true,
        applicationServerKey,
      });
      const raw = sub.toJSON();
      await api.post('/push/subscribe', {
        endpoint: raw.endpoint,
        keys: raw.keys,
      });
      setSubscribed(true);
      toast.success('Notifications activées 🔔');
      // Fire a test push automatically
      try { await api.post('/push/test'); } catch { /* ignore */ }
    } catch (e) {
      console.error(e);
      toast.error('Impossible d\'activer les notifs');
    } finally {
      setLoading(false);
    }
  };

  const disable = async () => {
    setLoading(true);
    try {
      const reg = await navigator.serviceWorker.ready;
      const sub = await reg.pushManager.getSubscription();
      if (sub) {
        await api.post('/push/unsubscribe', { endpoint: sub.endpoint });
        await sub.unsubscribe();
      }
      setSubscribed(false);
      toast('Notifications désactivées', { icon: '🔕' });
    } catch (e) {
      toast.error('Erreur');
    } finally {
      setLoading(false);
    }
  };

  // On iOS Safari NOT running as PWA → show install hint (push impossible otherwise)
  if (isIos && !isStandalone) {
    return (
      <div
        data-testid="push-ios-hint"
        style={{
          padding: '10px 14px', borderRadius: 10,
          background: 'rgba(245,166,35,0.10)',
          border: '1px solid rgba(245,166,35,0.35)',
          fontFamily: 'Sora', fontWeight: 600, fontSize: 12, color: '#F5A623',
          display: 'flex', alignItems: 'center', gap: 8,
          lineHeight: 1.35,
        }}
      >
        <Share size={14} strokeWidth={2} />
        <span>
          Pour activer les notifs sur iPhone : appuie sur <b>Partager</b> puis <b>« Sur l&apos;écran d&apos;accueil »</b>, puis rouvre ASA depuis l&apos;icône.
        </span>
      </div>
    );
  }

  if (!supported) {
    return (
      <div
        data-testid="push-unsupported"
        style={{
          padding: '10px 14px', borderRadius: 10,
          background: 'var(--sf2)', border: '1px solid var(--bd)',
          fontFamily: 'Sora', fontSize: 12, color: 'var(--t3)',
        }}
      >
        Notifications push non supportées par ce navigateur.
      </div>
    );
  }

  const btnStyle = {
    display: 'flex', alignItems: 'center', gap: 8,
    padding: compact ? '7px 12px' : '10px 16px',
    borderRadius: 999,
    fontFamily: 'Sora', fontWeight: 700, fontSize: compact ? 11.5 : 13,
    cursor: loading ? 'wait' : 'pointer',
    opacity: loading ? 0.7 : 1,
    transition: 'all 0.15s',
  };

  if (subscribed) {
    return (
      <button
        data-testid="push-disable-btn"
        onClick={disable}
        disabled={loading}
        style={{
          ...btnStyle,
          background: 'rgba(52,211,153,0.14)',
          border: '1.5px solid rgba(52,211,153,0.40)',
          color: '#34D399',
        }}
      >
        <Bell size={compact ? 12 : 14} strokeWidth={2} />
        {compact ? 'Actives' : 'Notifs actives — désactiver'}
      </button>
    );
  }

  return (
    <button
      data-testid="push-enable-btn"
      onClick={enable}
      disabled={loading}
      style={{
        ...btnStyle,
        background: 'rgba(91,190,252,0.14)',
        border: '1.5px solid rgba(91,190,252,0.40)',
        color: '#5BBEFC',
      }}
    >
      <BellOff size={compact ? 12 : 14} strokeWidth={2} />
      {compact ? 'Activer' : 'Activer les notifications'}
    </button>
  );
}
